import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabasePartner } from '../../lib/supabase';
import { labelBase, borderBase, inputBase } from '../../components/shared/helpers';

const errText = 'font-dm-sans text-sm text-white/55 font-light leading-relaxed';

// Phase 1d — partner password reset, screen 1 (request a reset link). Uses the
// isolated supabasePartner client only; never the anon customer client.
export default function PartnerForgotPassword() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Once true, we replace the form with the neutral, anti-enumeration message.
  const [sent, setSent] = useState(false);

  const handleSubmit = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');
    setSubmitError('');
    setSubmitting(true);
    try {
      // Anti-enumeration: we deliberately ignore whether Supabase reports the
      // email as existing or not. A "user not found" style error must NOT be
      // surfaced — we show the same neutral success message regardless. Only a
      // genuine failure (network, etc.) thrown from the call reaches catch.
      await supabasePartner.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: window.location.origin + '/partner/reset',
      });
      setSent(true);
    } catch (err) {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter">
      <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">

        <p className="font-dm-sans text-[12px] uppercase tracking-[0.18em] text-[rgba(201,168,76,0.85)] mb-2">
          Partner Portal
        </p>
        <h1 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-1.5">
          Reset your password
        </h1>

        {sent ? (
          <p className="font-dm-sans text-sm text-white/70 font-light mt-3 leading-relaxed">
            If an account exists for that email, we've sent a password reset link.
            Please check your inbox (and spam).
          </p>
        ) : (
          <>
            <p className="font-dm-sans text-sm text-white/55 font-light mb-7 leading-relaxed">
              Enter your account email and we'll send you a link to set a new password.
            </p>

            <div className="group flex flex-col gap-2">
              <label className={labelBase}>Email</label>
              <div className={borderBase}>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
                  className={inputBase}
                  autoComplete="email"
                />
              </div>
              {emailError && <p className={errText}>{emailError}</p>}
            </div>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full mt-8 py-[14px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-medium text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.97] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
            >
              {submitting ? 'Sending…' : 'Send reset link'}
            </button>

            {submitError && (
              <p className="font-dm-sans text-center text-sm text-white/55 mt-3.5 leading-relaxed">
                {submitError}
              </p>
            )}
          </>
        )}

        <p className="text-center mt-6">
          <Link
            to="/partner/login"
            className="font-dm-sans text-sm font-medium text-[#D4A843] hover:text-[#C9A84C] transition-colors duration-300"
          >
            Back to sign in
          </Link>
        </p>

        <div className="mt-6 pt-4 border-t border-white/[0.07] text-center">
          <Link
            to="/"
            className="font-dm-sans text-[13px] text-white/40 hover:text-[rgba(201,168,76,0.85)] transition-colors duration-300"
          >
            ← Back to main site
          </Link>
        </div>

      </div>
    </div>
  );
}
